import React, {Fragment, useEffect, useMemo, useState} from "react";
import {useFieldArray, useFormContext} from "react-hook-form";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose, faMinus, faPlus} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Utils
import {toTitleCase} from "../../utils/helpers";

// Components
import SearchSelect from "../../components/SearchSelect";
import {InputSelect, InputError, InputCalendar} from "../../components/form/FormInputs.js";

// Style
import {border, pad, radius} from "../../style/components/variables";
import {Abbr, Button, Inline, Warning} from "../../style/components/general";
import {flex} from "../../style/components/mixins";
import {voice} from "../../style/components/typography";

const noLastDayOption = ["daily", "weekly", "bi-weekly", "work-week", "semi-monthly"];

const BulkApplyUnit = ({
  defaultEvent,
  template,
  index,
  eventTypes,
  groups,
  frequencies,
  allTemplates,
  hasSubmitted
}) => {
  const templateMap = {};

  const [templateResults, setTemplateResults] = useState(
    allTemplates.map(t => {
      templateMap[t.id] = t;
      return {name: t.id, label: t.name};
    })
  );

  const [groupResults, setGroupResults] = useState([]);
  const {
    watch,
    control,
    setValue,
    trigger,
    formState: {errors}
  } = useFormContext();

  const {insert, remove} = useFieldArray({control, name: "templates"});

  const templates = watch("templates");
  const frequency = watch(`templates.${index}.frequency`);
  const startDate = watch(`templates.${index}.startDate`);
  const type = watch(`templates.${index}.type`);
  const group = watch(`templates.${index}.group`);

  const eventType = useMemo(() => eventTypes.find(t => t.name === type), [eventTypes, type]);

  useEffect(() => {
    if (hasSubmitted.current) trigger();
  }, [frequency, startDate, hasSubmitted, trigger]);

  useEffect(() => {
    if (frequency === "semi-monthly") {
      let start = dayjs();

      const daysInMonth = start.daysInMonth();
      start =
        start.date() < daysInMonth ? start.date(Math.floor(daysInMonth / 2)) : start.endOf("month");

      const midCurrentMonth = Math.floor(daysInMonth);

      if (start.date() !== midCurrentMonth && start.date() !== start.daysInMonth()) {
        if (start.date() > midCurrentMonth) {
          start = start.date(1);
          start = start.add(1, "month");

          const midNextMonth = Math.floor(start.daysInMonth() / 2);
          start.date(midNextMonth);
        } else {
          start = start.date(start.daysInMonth());
        }

        setValue(`templates.${index}.startDate.date`, start.format("YYYY-MM-DD"));
        setValue(`templates.${index}.startDate.visibleDate`, start.format("MM/DD/YYYY"));
      }
    }

    if (frequency === "quarterly") {
      let start = dayjs();

      const currentMonth = start.month();
      let nextMonth = currentMonth;

      if (nextMonth >= 0 && nextMonth < 3) nextMonth = 2;
      if (nextMonth >= 3 && nextMonth < 6) nextMonth = 5;
      if (nextMonth >= 6 && nextMonth < 9) nextMonth = 8;
      else nextMonth = 11;

      start = start.date(1);
      start = start.month(nextMonth);

      const quarterEnd = start.daysInMonth();
      start = start.date(quarterEnd);

      setValue("start.date", start.format("YYYY-MM-DD"));
      setValue("start.visibleDate", start.format("MM/DD/YYYY"));
    }
  }, [frequency, setValue, index]);

  const searchTemplates = query => {
    if (query)
      setTemplateResults(
        allTemplates
          .filter(({name}) => name.toLowerCase().includes(query.toLowerCase()))
          .map(({id, name}) => ({name: id, label: name}))
      );
    else setTemplateResults(allTemplates.map(({id, name}) => ({name: id, label: name})));
  };

  const searchGroups = query => {
    if (query)
      setGroupResults(
        groups.filter(({label}) => label.toLowerCase().includes(query.toLowerCase()))
      );
    else setGroupResults(groups);
  };

  const applyTemplate = ({type: localType, name: eventName, responses, stages, ...rest}, i) => {
    setValue(`templates.${i}`, {
      ...defaultEvent,
      type: localType.name,
      name: eventName,
      responses,
      stages,
      group,
      ...rest
    });

    if (hasSubmitted.current) trigger();
  };

  const addGroup = (g, i) => {
    setValue(`templates.${i}.group`, g);

    if (hasSubmitted.current) trigger();
  };

  return (
    <Fragment key={`template-${index}`}>
      <TemplateWrapper>
        <InputGroup>
          <SearchInPlace>
            {!template.name && (
              <SearchSelect
                results={templateResults}
                setResults={setTemplateResults}
                search={searchTemplates}
                add={({name}) => applyTemplate(templateMap[name], index)}
                placeholder="Name..."
                showAll
                showMore
              />
            )}
            {(template.name || template.label) && (
              <Applied
                type="button"
                title="Unapply Template"
                onClick={() => {
                  setValue(`templates.${index}`, {
                    ...defaultEvent,
                    frequency,
                    startDate,
                    group
                  });
                }}>
                <span>
                  <Abbr title={template.label || template.name}>
                    {template.label || template.name}
                  </Abbr>
                </span>
                {eventType && (
                  <EventType type="button" title={eventType.name} color={`#${eventType.color}`}>
                    {toTitleCase(eventType.name)}
                  </EventType>
                )}
                <FontAwesomeIcon icon={faClose} />
              </Applied>
            )}
          </SearchInPlace>
          <SearchInPlace>
            {!group?.label && (
              <SearchSelect
                results={groupResults}
                setResults={setGroupResults}
                search={searchGroups}
                add={t => addGroup(t, index)}
                placeholder="Group..."
                showAll
                showMore
              />
            )}
            {group?.label && (
              <Applied
                type="button"
                title="Deselect Group"
                onClick={() => {
                  setValue(`templates.${index}.group`, null);
                }}>
                <span>
                  <Abbr title={group.label}>{group.label}</Abbr>
                </span>
                <FontAwesomeIcon icon={faClose} />
              </Applied>
            )}
          </SearchInPlace>
          <SelectWrapper>
            <InputSelect
              name={`templates.${index}.frequency`}
              placeholder="Frequency..."
              options={
                frequencies
                  ? [
                      ...frequencies.map(freq => ({
                        name: freq.name,
                        label: freq.label
                      }))
                    ]
                  : []
              }
              minWidth={150}
            />
          </SelectWrapper>
          <DateWrapper>
            <InputCalendar
              name={`templates.${index}.startDate`}
              showLastDay={!!frequency && !noLastDayOption.includes(frequency)}
              placeholder="First Due Date..."
              disabled={frequency === "semi-monthly"}
            />
          </DateWrapper>
        </InputGroup>
        <ButtonBar>
          <StyledEdit disable={templates.length === 1} type="button" onClick={() => remove(index)}>
            <FontAwesomeIcon icon={faMinus} />
          </StyledEdit>
          <StyledEdit
            type="button"
            onClick={() => {
              insert(index + 1, defaultEvent);
            }}>
            <FontAwesomeIcon icon={faPlus} />
          </StyledEdit>
        </ButtonBar>
      </TemplateWrapper>

      {frequency === "semi-monthly" && (
        <SemiMonthlyWarning>
          <WarningHeader>Warning!</WarningHeader>&nbsp;Start Date defaults to the middle or last day
          of the month for semi-monthly events.
        </SemiMonthlyWarning>
      )}
      {frequency === "quarterly" && (
        <SemiMonthlyWarning>
          <WarningHeader>Warning!</WarningHeader>&nbsp;Start Date defaults to the last day of the
          quarter for quarterly events.
        </SemiMonthlyWarning>
      )}
      <InputError name={`templates.${index}.name`} errors={errors} />
    </Fragment>
  );
};

BulkApplyUnit.propTypes = {
  defaultEvent: PropTypes.objectOf(PropTypes.any).isRequired,
  template: PropTypes.objectOf(PropTypes.any).isRequired,
  index: PropTypes.number.isRequired,
  eventTypes: PropTypes.arrayOf(PropTypes.any),
  groups: PropTypes.arrayOf(PropTypes.any),
  frequencies: PropTypes.arrayOf(PropTypes.any),
  allTemplates: PropTypes.arrayOf(PropTypes.any),
  hasSubmitted: PropTypes.objectOf(PropTypes.any)
};

BulkApplyUnit.defaultProps = {
  eventTypes: [],
  groups: [],
  frequencies: [],
  allTemplates: [],
  hasSubmitted: null
};

// Style Overrides
const DateWrapper = styled.div`
  width: 200px;

  margin-right: -${pad}px;
`;

const TemplateWrapper = styled(Inline)`
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
  padding: ${pad * 1.4}px;
  width: 100%;

  justify-content: space-between;
`;

const InputGroup = styled(Inline)`
  width: calc(100% - 100px);
`;

const ButtonBar = styled(Inline)`
  width: 100px;

  justify-content: end;
`;

const StyledEdit = styled(Button)`
  padding: ${pad / 2}px ${pad}px;

  ${props =>
    props.disable &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const SearchInPlace = styled.div`
  width: 28%;
  max-width: 300px;
  min-width: 150px;
`;

const SelectWrapper = styled.div`
  margin-top: -${pad}px;
  width: 28%;
  max-width: 300px;
  min-width: 150px;

  input {
    max-width: 300px !important;
  }
`;

const Applied = styled(Button)`
  ${flex("row", "nowrap", "space-between", "center")};
  width: 100%;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EventType = styled.div`
  border-radius: ${radius};
  border: ${border} solid ${({color}) => color};
  margin-right: ${pad / 2}px;
  color: ${({theme}) => theme.tertiary};
  background: ${({color}) => color};
  padding: 0;
  ${voice.quiet};
`;

const SemiMonthlyWarning = styled(Warning)`
  margin-top: ${pad}px;
  color: ${({theme}) => theme.secondary};
`;

const WarningHeader = styled.strong`
  color: ${({theme}) => theme.warning};
`;

export default BulkApplyUnit;
